import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { FormsModule } from '@angular/forms';

import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

import { AdminService } from '@generated/services/admin.service';
import { FilesService } from '@generated/services/files.service';
import { BenchmarkSttSampleDto } from '@generated/models/benchmark-stt-sample-dto';
import { SttBenchmarkCase } from '@generated/models/stt-benchmark-case';
import { FileType } from '@generated/models/file-type';
import { NotificationService } from '@core/services/notification.service';
import { AudioPlayer } from '@shared/components/audio-player/audio-player';

/**
 * Управление STT эталонными семплами внутри одного бенчмарка.
 *
 * Flow загрузки:
 *   1) POST /api/files/generate-upload-url (тот же endpoint что test-stt),
 *      file_type = 'SttTestAudio' — бэк готов принять аудио в бенч через
 *      этот тип.
 *   2) PUT audio bytes на presigned URL из ответа.
 *   3) POST /api/Admin/benchmarks/{benchmarkId}/stt-samples с
 *      { fileId, title, case, languageLocaleId, expectedText }. Бэк
 *      конвертит в WAV 16k mono, парсит длительность, сохраняет и
 *      оригинал, и converted.
 *
 * Родитель (benchmark-edit) отдаёт benchmarkId и текущий список семплов
 * из detail'а; после успешной заливки — эмитим (added) чтобы parent
 * подтянул свежий состав (через локальный append либо reload detail).
 */
@Component({
  selector: 'app-benchmark-stt-samples',
  standalone: true,
  imports: [
    FormsModule,
    ButtonModule,
    TableModule,
    TagModule,
    TooltipModule,
    SelectModule,
    InputTextModule,
    TextareaModule,
    ProgressSpinnerModule,
    AudioPlayer,
  ],
  templateUrl: './benchmark-stt-samples.component.html',
  styleUrl: './benchmark-stt-samples.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BenchmarkSttSamplesComponent {
  private adminService = inject(AdminService);
  private filesService = inject(FilesService);
  private notificationService = inject(NotificationService);
  private cdr = inject(ChangeDetectorRef);

  readonly benchmarkId = input.required<string>();
  readonly samples = input<BenchmarkSttSampleDto[]>([]);
  readonly added = output<BenchmarkSttSampleDto>();
  /** Локальное удаление — parent убирает id из sttSampleIds, а Save
   *  отправит актуальный набор через `stt_sample_ids` (full-replace). */
  readonly removed = output<string>();

  @ViewChild('fileInput') private fileInputRef?: ElementRef<HTMLInputElement>;

  // Форма новой заливки. draftBlob хранит либо выбранный File (accept audio/*),
  // либо записанный через MediaRecorder Blob (audio/webm;opus). Оба варианта
  // одинаково грузятся presigned PUT'ом. UX тот же, что в STT-тестере.
  readonly draftBlob = signal<Blob | null>(null);
  readonly draftUrl = signal<string | null>(null);
  readonly draftFileName = signal<string>('');
  readonly isRecording = signal<boolean>(false);
  private mediaRecorder: MediaRecorder | null = null;
  private mediaStream: MediaStream | null = null;
  private recordedChunks: Blob[] = [];

  readonly draftTitle = signal<string>('');
  readonly draftCase = signal<SttBenchmarkCase>('Quiet');
  readonly draftExpected = signal<string>('');
  readonly isUploading = signal<boolean>(false);

  /** id-ы сэмплов, у которых юзер раскрыл полный эталонный текст.
   *  По умолчанию текст clamp'нут в 2 строки, кнопка «Показать полностью»
   *  добавляет id сюда. */
  readonly expandedIds = signal<Set<string>>(new Set());

  toggleExpanded(id: string | null | undefined): void {
    if (!id) return;
    const next = new Set(this.expandedIds());
    if (next.has(id)) next.delete(id); else next.add(id);
    this.expandedIds.set(next);
  }

  isExpanded(id: string | null | undefined): boolean {
    return !!id && this.expandedIds().has(id);
  }

  readonly caseOptions: Array<{ label: string; value: SttBenchmarkCase }> = [
    { label: $localize`:@@benchmarks.stt.case.quiet:Тихо`, value: 'Quiet' },
    { label: $localize`:@@benchmarks.stt.case.roadside:У дороги`, value: 'RoadSide' },
    { label: $localize`:@@benchmarks.stt.case.car:В машине`, value: 'Car' },
    { label: $localize`:@@benchmarks.stt.case.tv:У ТВ`, value: 'TV' },
    { label: $localize`:@@benchmarks.stt.case.speaker:Громкая связь`, value: 'Speakerphone' },
  ];

  readonly canUpload = computed(() =>
    !!this.draftBlob()
    && !this.isRecording()
    && this.draftTitle().trim().length > 0
    && this.draftExpected().trim().length > 0
    && !this.isUploading()
  );

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.clearDraftBlob();
    if (file) {
      this.draftBlob.set(file);
      this.draftUrl.set(URL.createObjectURL(file));
      this.draftFileName.set(file.name);
      // Автозаполняем title именем файла если оно ещё пустое.
      if (!this.draftTitle().trim()) {
        this.draftTitle.set(file.name.replace(/\.[^.]+$/, ''));
      }
    }
    this.cdr.markForCheck();
  }

  async startRecording(): Promise<void> {
    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.recordedChunks = [];
      this.clearDraftBlob();
      this.mediaRecorder = new MediaRecorder(this.mediaStream, { mimeType: 'audio/webm;codecs=opus' });
      this.mediaRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) this.recordedChunks.push(e.data);
      };
      this.mediaRecorder.onstop = () => {
        const blob = new Blob(this.recordedChunks, { type: 'audio/webm' });
        const fileName = `sample-${Date.now()}.webm`;
        this.draftBlob.set(blob);
        this.draftUrl.set(URL.createObjectURL(blob));
        this.draftFileName.set(fileName);
        this.stopMicStream();
        this.cdr.markForCheck();
      };
      this.mediaRecorder.start();
      this.isRecording.set(true);
      this.cdr.markForCheck();
    } catch (e) {
      console.error('Mic access failed', e);
      this.notificationService.showError(
        $localize`:@@benchmarks.stt.micFailed:Не удалось получить доступ к микрофону`
      );
    }
  }

  stopRecording(): void {
    this.mediaRecorder?.stop();
    this.isRecording.set(false);
    this.cdr.markForCheck();
  }

  private stopMicStream(): void {
    this.mediaStream?.getTracks().forEach(t => t.stop());
    this.mediaStream = null;
  }

  discardDraft(): void {
    this.clearDraftBlob();
    this.cdr.markForCheck();
  }

  private clearDraftBlob(): void {
    const prevUrl = this.draftUrl();
    if (prevUrl) URL.revokeObjectURL(prevUrl);
    this.draftBlob.set(null);
    this.draftUrl.set(null);
    this.draftFileName.set('');
  }

  clearDraft(): void {
    this.clearDraftBlob();
    this.draftTitle.set('');
    this.draftCase.set('Quiet');
    this.draftExpected.set('');
    if (this.fileInputRef?.nativeElement) this.fileInputRef.nativeElement.value = '';
  }

  async upload(): Promise<void> {
    if (!this.canUpload()) return;
    const blob = this.draftBlob()!;
    const fileName = this.draftFileName() || `sample-${Date.now()}.webm`;
    const contentType = blob.type || 'audio/wav';
    const title = this.draftTitle().trim();
    const expected = this.draftExpected().trim();
    const caseValue = this.draftCase();

    this.isUploading.set(true);
    this.cdr.markForCheck();

    try {
      // 1) Presigned URL. file_type=SttTestAudio — тот же bucket, что и
      //    у test-stt (бэк принимает).
      const uploadInfo = await this.filesService.apiFilesGenerateUploadUrlPost({
        body: {
          file_name: fileName,
          content_type: contentType,
          file_type: 'SttTestAudio' as FileType,
        }
      }).toPromise();

      if (!uploadInfo?.upload_url || !uploadInfo?.file_id) {
        throw new Error('Failed to get upload URL');
      }

      // 2) PUT bytes на presigned.
      const putRes = await fetch(uploadInfo.upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body: blob,
      });
      if (!putRes.ok) throw new Error(`Upload failed: ${putRes.status}`);

      // 3) POST /stt-samples. Бэк конвертит в WAV 16k mono и сохраняет.
      const created = await this.adminService.apiAdminBenchmarksBenchmarkIdSttSamplesPost({
        benchmarkId: this.benchmarkId(),
        body: {
          file_id: uploadInfo.file_id,
          title,
          case: caseValue,
          expected_text: expected,
        }
      }).toPromise();

      this.notificationService.showSuccess(
        $localize`:@@benchmarks.stt.uploadSuccess:Семпл добавлен`
      );
      // Emit added — parent обновит список без reload'а detail'а.
      // BenchmarkSttSampleDto собираем из create-result + form.
      const sample: BenchmarkSttSampleDto = {
        id: created?.sample_id,
        title,
        case: caseValue,
        expected_text: expected,
        original_audio_object_storage_path: created?.original_audio_object_storage_path,
        audio_object_storage_path: created?.audio_object_storage_path,
        duration_ms: created?.duration_ms,
        sample_rate_hz: created?.sample_rate_hz,
        is_active: true,
      };
      this.added.emit(sample);
      this.clearDraft();
    } catch (error) {
      console.error('Failed to upload STT sample:', error);
      this.notificationService.showError(
        $localize`:@@benchmarks.stt.uploadFail:Не удалось загрузить семпл`
      );
    } finally {
      this.isUploading.set(false);
      this.cdr.markForCheck();
    }
  }

  // ===== template helpers =====

  caseLabel(code: string | null | undefined): string {
    switch ((code || '').toLowerCase()) {
      case 'quiet': return $localize`:@@benchmarks.stt.case.quiet:Тихо`;
      case 'roadside': return $localize`:@@benchmarks.stt.case.roadside:У дороги`;
      case 'car': return $localize`:@@benchmarks.stt.case.car:В машине`;
      case 'tv': return $localize`:@@benchmarks.stt.case.tv:У ТВ`;
      case 'speakerphone': return $localize`:@@benchmarks.stt.case.speaker:Громкая связь`;
      default: return code || '';
    }
  }

  formatDurationMs(value: number | null | undefined): string {
    if (value == null) return '';
    return `${(value / 1000).toFixed(1)}s`;
  }
}
